import * as React from 'react';
import { useSelectSensors, useSelectSensorsWithGroupedEmaginChannels, } from '../../selectors';
import { InputAutocompleteMulti, } from '..';
import InputAutocomplete from '../StylovyzeForm/InputAutocomplete';
/**
 * These are the filter functions that handle the filtering for each one of the properties defined at `SensorsAutocompleteFilters`.
 * */
const sensorFiltersMap = {
    sensorType: (sensor, filter) => {
        return Array.isArray(filter)
            ? filter.includes(sensor.sensorType)
            : sensor.sensorType === filter;
    },
    isPhysical: (sensor, filter) => {
        return sensor.isPhysical === filter;
    },
    resolution: (sensor, filter) => {
        const filters = Array.isArray(filter) ? filter : [filter];
        return filters.some(f => sensor.resolutions.includes(f));
    },
    excludeSourceType: (sensor, filter) => {
        const existInExcludedSourceType = Array.isArray(filter)
            ? filter.includes(sensor.sourceType || '')
            : sensor.sourceType === filter;
        return !existInExcludedSourceType;
    },
};
/**
 * Creates a filter function for sensors.
 *
 *  @param filters An object defining filter props and values. If multiple filters are provided, they all will be validated "in parallel" for each sensor.
 *
 * @param additionalFilters A filter function that you can use to run any validation on the sensor. If `filters` is also set, this function will be execcuted along with those validations.
 * */
const makeSensorsFilter = (filters, additionalFilters) => (sensor, index) => {
    const _filters = filters
        ? Object.entries(filters).reduce((reducedFilters, [filter, filterValue]) => {
            const filterFunc = sensorFiltersMap[filter];
            return filterFunc
                ? [...reducedFilters, { filterValue, filterFunc }]
                : reducedFilters;
        }, [])
        : [];
    if (additionalFilters) {
        _filters.push({
            filterFunc: additionalFilters,
            filterValue: index,
        });
    }
    return _filters.length > 0
        ? [..._filters].every(({ filterFunc, filterValue }) => filterFunc(sensor, filterValue))
        : true;
};
const sensorToOption = (sensor) => {
    return {
        key: sensor.sensorId,
        value: sensor.sensorAlias || sensor.sensorId,
        schedulesOptions: sensor.schedulesOptions,
    };
};
/**
 * Add a description for your component here
 */
export const SensorsForecastAutocomplete = (props) => {
    const { additionalFilters, autocompleteProps, filters, label, onChange, textFieldProps, mode = 'single', ...restOfProps } = props;
    const { sensors, secondaryEmaginChannels } = useSelectSensorsWithGroupedEmaginChannels();
    const { initialized } = useSelectSensors();
    const [options, setOptions] = React.useState([]);
    const additionalFiltersRef = React.useRef(additionalFilters);
    const memoizedFilters = React.useMemo(() => {
        return filters;
    }, [
        filters?.isPhysical,
        Array.isArray(filters?.sensorType)
            ? filters?.sensorType?.join('')
            : filters?.sensorType,
        Array.isArray(filters?.resolution)
            ? filters?.resolution?.join('')
            : filters?.resolution,
        Array.isArray(filters?.excludeSourceType)
            ? filters?.excludeSourceType?.join('')
            : filters?.excludeSourceType,
    ]);
    React.useEffect(() => {
        additionalFiltersRef.current = additionalFilters;
    }, [additionalFilters]);
    React.useEffect(() => {
        const hasFilters = Object.keys(memoizedFilters ?? {}).length > 0 ||
            additionalFiltersRef.current;
        const _sensors = hasFilters
            ? sensors.filter(makeSensorsFilter(memoizedFilters, additionalFiltersRef.current))
            : sensors;
        const _options = [];
        /**
         * There's some sensors that don't have id and this breaks the app.
         *
         * It should be fixed on the backend...but for now, we have to do this.
         * Keep the good old for, is faster and there may be thousands of sensors.
         * */
        for (const element of _sensors) {
            if (element.sensorId) {
                _options.push(sensorToOption(element));
            }
        }
        setOptions(_options);
    }, [sensors, initialized, memoizedFilters, additionalFiltersRef]);
    const hiddenOptions = secondaryEmaginChannels.map(channel => ({
        key: channel.sensorId,
        value: channel.sensorAlias,
    }));
    if (mode === 'single') {
        return (React.createElement(InputAutocomplete, { ...restOfProps, label: label || 'Sensors', options: options, hiddenOptions: hiddenOptions, onChange: (e, o) => {
                const newOption = o ? [o] : undefined;
                onChange?.(e, newOption);
            }, textFieldProps: {
                ...textFieldProps,
                onBlur: textFieldProps?.onBlur,
            }, autocompleteProps: {
                disabled: !initialized,
                ...autocompleteProps,
                onBlur: autocompleteProps?.onBlur,
            }, getOptionSelected: (opt, val) => {
                if (!val.key)
                    return false;
                if (val.key === opt.key)
                    return true;
                if (opt.schedulesOptions) {
                    for (const option of opt.schedulesOptions) {
                        if (option.info360ChannelId === val.key) {
                            return true;
                        }
                    }
                }
                return false;
            } }));
    }
    if (mode === 'multiple') {
        return (React.createElement(InputAutocompleteMulti, { ...restOfProps, label: label || 'Sensors', options: options, onChange: (e, o) => {
                onChange?.(e, o);
            }, textFieldProps: {
                ...textFieldProps,
                onBlur: textFieldProps?.onBlur,
            }, autocompleteProps: {
                disabled: !initialized,
                ...autocompleteProps,
                onBlur: autocompleteProps?.onBlur,
            } }));
    }
    return React.createElement(React.Fragment, null);
};
export default SensorsForecastAutocomplete;
